<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";

const props = withDefaults(
  defineProps<{
    extensions?: string[];
    multiple?: boolean;
    description?: string;
  }>(),
  {
    extensions: () => [],
    multiple: true,
  }
);

const emit = defineEmits<{
  (e: "files", files: File[]): void;
}>();

const isDragging = ref(false);
const dragCounter = ref(0);

function handleGlobalDragEnter(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  dragCounter.value++;
  isDragging.value = true;
}

function handleGlobalDragLeave(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  dragCounter.value = Math.max(0, dragCounter.value - 1);
  if (dragCounter.value === 0) {
    isDragging.value = false;
  }
}

function handleGlobalDrop(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  dragCounter.value = 0;
  isDragging.value = false;
  if (e.dataTransfer) handleFiles(e.dataTransfer.files);
}

function handleFiles(files: FileList) {
  let validatedFiles = Array.from(files).filter((file) => {
    const extension = file.name.split(".").pop()?.toLowerCase();
    return (
      props.extensions.length === 0 ||
      props.extensions.includes(extension || "")
    );
  });

  if (!props.multiple && validatedFiles.length > 0) {
    validatedFiles = [validatedFiles[0]];
  }

  emit("files", validatedFiles);
}

onMounted(() => {
  window.addEventListener("dragenter", handleGlobalDragEnter);
  window.addEventListener("dragleave", handleGlobalDragLeave);
  window.addEventListener("drop", handleGlobalDrop);
});

onUnmounted(() => {
  window.removeEventListener("dragenter", handleGlobalDragEnter);
  window.removeEventListener("dragleave", handleGlobalDragLeave);
  window.removeEventListener("drop", handleGlobalDrop);
});
</script>

<template>
  <div
    v-if="isDragging"
    id="fullscreen-uploader"
    @drop="handleGlobalDrop"
    @dragover.prevent
  >
    <div class="content text-center">
      <el-icon size="5rem">
        <i class="bi-cloud-arrow-down-fill"></i>
      </el-icon>
      <p class="h3">
        {{ multiple ? $t("drag.your.files") : $t("drag.your.file") }}
      </p>
      <p v-if="description">{{ description }}</p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
#fullscreen-uploader {
  position: fixed;
  inset: 0;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 9999;
  pointer-events: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.3s;
}
</style>
